import React, { useState, useCallback, useEffect } from "react";
import MaxWidthWrapper from "../common/MaxWidthWrapper";
import SecondaryWidthWrapper from "../common/SecondaryWidthWrapper";
import ContactPageBGCurve from "../../images/curve/ContactPageBGCurve";
import GeographicMapSvg from "../svg/GeographicMap";
import LoaderSvg from "../svg/loader.svg";
//@ts-ignore
import heart from "../../images/homePage/heart.svg";
import Select from "react-select";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import axios from "axios";
import sha256 from "crypto-js/sha256";
import Hex from "crypto-js/enc-hex";
import { useFormik } from "formik";
import * as yup from "yup";
import { emailRegex } from "../../util/mix";
import { Link } from "gatsby";
import { parsePhoneNumber } from "libphonenumber-js";
import { getLocation } from "../../util/location";
import { useAdMetadata } from "../../hooks/useMarketingIds";
import { v4 as uuidv4 } from "uuid";

function normalizeAndHash(data: string, type: 'email' | 'phone' | 'name'): string {
  let normalized = data.trim();

  switch (type) {
    case 'email':
      normalized = normalized.toLowerCase();
      break;
    case 'phone':
      // Remove all non-digit characters except +
      normalized = normalized.replace(/[^\d+]/g, '');
      // Ensure it starts with + for E.164 format
      if (!normalized.startsWith('+')) {
        // Add default country code if missing (assume +1 for US/Canada, adjust as needed)
        normalized = `+91${normalized}`;
      }
      break;
    case 'name':
      normalized = normalized.toLowerCase();
      break;
  }

  const hashed = sha256(normalized);
  return hashed.toString(Hex);
}

const schema = yup.object().shape({
  purpose: yup.object().nullable().required("Purpose is required"),
  name: yup.string().required("Name is required"),
  phone: yup
    .string()
    .required("Phone number is required")
    .max(15, "Please enter valid phone number"),
  email: yup
    .string()
    .email()
    .required("Email is required")
    .matches(emailRegex, "Please enter valid email"),
  companyName: yup.string().required("Company name is required"),
  message: yup.string().required("Message is required"),
  heardFrom: yup
    .object()
    .nullable()
    .required("Please let us know how you heard about us"),
});

interface ContactUsProps {
  title: string;
  titleGreen: string;
  description?: string;
  titleBreak?: boolean;
  tag?: boolean;
  ModelclassName?: string;
  isModal?: boolean;
  setformSubmitted?: (submitted: boolean) => void;
}

function gtag_report_conversion(userData: Record<string, string>, orderId: string) {
  // Only run in production environment
  if (process.env.APP_ENV !== "prod") {
    console.log(
      "Google Ads conversion tracking skipped in non-production environment"
    );
    return false;
  }

  // @ts-ignore
  window.gtag("event", "conversion", {
    send_to: "AW-386864266/1d6eCIXaxqcZEIqpvLgB",
    transaction_id: orderId,
    user_data: {
      name: normalizeAndHash(userData.name, 'name'),
      phone_number: normalizeAndHash(userData.phone, 'phone'),
      email: normalizeAndHash(userData.email, 'email'),
    },
  });
  return false;
}

const customStyles = {
  placeholder: (defaultStyles: any) => {
    return {
      ...defaultStyles,
      color: "#9CA4B7",
    };
  },
  control: (provided: any, _: any) => ({
    ...provided,
    border: "none",
    boxShadow: "none",
    color: "red",
    textOverflow: "ellipsis",
    backgroundColor: "transparent",
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#40BA77" : "white",
    boxBorder: "1px solid #40BA77",
    "&:hover": {
      backgroundColor: "#8ed7af",
      color: "white",
    },
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 9999,
    width: "100%",
  }),
  menuList: (provided: any) => ({
    ...provided,
    maxHeight: "300px",
    width: "100%",
  }),
};

const FormSubmitted = () => (
  <div className="ml-[-35px]">
    <svg
      width="400"
      height="200"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 400 200"
    >
      <rect width="100%" height="100%" fill="transparent" />
      <g transform="translate(200, 70)">
        <circle cx="0" cy="0" r="50" fill="#4caf50">
          <animate
            attributeName="r"
            from="0"
            to="50"
            dur="0.5s"
            fill="freeze"
          />
        </circle>
        <text
          x="0"
          y="10"
          fontFamily="cursive"
          fontSize="40"
          textAnchor="middle"
          fill="#fff"
        >
          ✓
          <animate
            attributeName="opacity"
            from="0"
            to="1"
            begin="0.5s"
            dur="0.5s"
            fill="freeze"
          />
        </text>
      </g>
      <text
        x="210"
        y="160"
        fontSize="24"
        textAnchor="middle"
        fill="#333"
        className=" text-primary"
      >
        We will get back to you soon!
        <animate
          attributeName="opacity"
          from="0"
          to="1"
          begin="1s"
          dur="0.5s"
          fill="freeze"
        />
      </text>
    </svg>
  </div>
);

const ContactUs: React.FC<ContactUsProps> = ({
  titleGreen,
  title,
  description,
  titleBreak = true,
  tag = false,
  ModelclassName = "relative mt-[80px] flex flex-col justify-center py-[55px] px-[10px] md:px-[80px] xl:flex-row xl:gap-x-11",
  isModal,
  setformSubmitted,
}) => {
  const adMetadata = useAdMetadata();
  const [submitted, setSubmitted] = useState(false);
  const [initialPurpose, setInitialPurpose] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [location, setLocation] = useState<{
    city: string;
    state: string;
    country: string;
    countryCode: string;
    source: string;
  } | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const currentPath = window.location.pathname.replace(/\//g, "");
      const matchedPurpose = selectOptions.find(
        (option) => option.value === currentPath
      );
      setInitialPurpose(matchedPurpose || null);
    }
  }, []);

  useEffect(() => {
    const fetchLocation = async () => {
      const locationData = await getLocation();
      if (locationData) {
        setLocation({
          city: locationData.city,
          state: locationData.state,
          country: locationData.country,
          countryCode: locationData.countryCode,
          source: locationData.source,
        });
      }
    };
    fetchLocation();
  }, []);

  const selectOptions = [
    {
      value: "staff-augmentation",
      label: "Looking to hire remote software engineers",
    },
    {
      value: "mvp",
      label: "Looking for a team to develop a MVP/Website/Mobile App",
    },
    {
      value: "joinus",
      label: "Looking for a Job at Propelius Technologies",
    },
    {
      value: "ai-automation",
      label: "Looking to automate business using AI",
    },
    {
      value: "build-a-website-for-my-business",
      label: "I want to build a Lightning Fast Website",
    },
    {
      value: "other",
      label: "Other",
    },
  ];

  const heardFromOptions = [
    {
      value: "friend_colleague",
      label: "Through a friend/colleague",
    },
    {
      value: "google",
      label: "Google",
    },
    {
      value: "facebook",
      label: "Facebook",
    },
    {
      value: "instagram",
      label: "Instagram",
    },
    {
      value: "linkedin",
      label: "LinkedIn",
    },
    {
      value: "upwork",
      label: "Upwork",
    },
    {
      value: "fiverr",
      label: "Fiverr",
    },
    {
      value: "other",
      label: "Other",
    },
  ];

  const tags = [
    "2-Week Risk-Free Trial",
    "Save 70%",
    "Run by a Startup Architect",
  ];

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log("Execute recaptcha not yet available");
      return;
    }
    return await executeRecaptcha("homepage");
  }, [executeRecaptcha]);

  const formik = useFormik({
    initialValues: {
      purpose: initialPurpose,
      name: "",
      companyName: "",
      email: "",
      phone: "",
      message: "",
      heardFrom: null as { value: string; label: string } | null,
    },
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        setSubmitting(true);
        const token = await handleReCaptchaVerify();

        if (values.email === "mail@mailinator.com" || values.email === "mail@mailnator.com") {
          console.log("Test email detected - skipping API calls");
          if (isModal) {
            setformSubmitted && setformSubmitted(true);
          }
          if (!isModal) {
            setSubmitted(true);
            setTimeout(() => setSubmitted(false), 5000);
          }
          resetForm();
          return;
        }

        const payload = {
          ...values,
          token,
          purpose: values.purpose?.label,
          heardFrom: values.heardFrom?.label,
          city: location?.city || "",
          state: location?.state || "",
          country: location?.country || "",
          ad_metadata: adMetadata || "",
        };

        const orderId = uuidv4();
        const enhancedPayload = {
          ...payload,
          conversion_id: orderId,
        };

        // Make both API calls in parallel for better performance and reduced chance of double submission
        const [contactResponse, notionResponse] = await Promise.all([
          axios.post(
          `${process.env.GATSBY_API_URL}/contact-users`,
          { data: enhancedPayload },
          { headers: { Authorization: `Bearer ${process.env.GATSBY_API_KEY}` } }
          ),
          axios.post(
            `${process.env.GATSBY_API_URL}/notion`,
            {
              name: values.name,
              companyName: values.companyName,
              phone: values.phone,
              email: values.email,
              purpose: values.purpose?.label || "",
              heardFrom: values.heardFrom?.label || "",
              message: values.message,
              city: location?.city || "",
              state: location?.state || "",
              country: location?.country || "",
              ad_metadata: adMetadata || "",
              conversion_id: orderId,
            }
          )
        ]);

        // Check responses
        if (!contactResponse.data) {
          throw new Error("Contact API failed");
        }

          const notionResult = notionResponse.data;
          if (notionResult?.error) {
            throw new Error("Couldn't submit enquiry");
          }

          gtag_report_conversion({
            ...values,
            purpose: values.purpose?.label || "",
            heardFrom: values.heardFrom?.label || "",
          }, orderId);

          if (isModal) {
            setformSubmitted && setformSubmitted(true);
          }

          if (!isModal) {
            setSubmitted(true);
            setTimeout(() => setSubmitted(false), 5000);
          }

          resetForm();
      } catch (err) {
        console.error(err);
        setErrorMsg("Something went wrong.");
        setTimeout(() => setErrorMsg(""), 4000);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <MaxWidthWrapper>
      <SecondaryWidthWrapper>
        <div className={ModelclassName}>
          <div className="absolute left-[20px] top-[20px] bottom-0 md:top-[10px] md:left-[90px]">
            <ContactPageBGCurve />
          </div>

          {isModal && (
            <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center">
              <GeographicMapSvg />
            </div>
          )}

          <div className={"z-10 flex flex-1"}>
            <div className={"relative w-full"}>
              {!isModal && (
                <div
                  className={
                    "absolute -z-10 flex h-full w-full items-center justify-center xl:h-0 xl:w-0"
                  }
                >
                  <GeographicMapSvg />
                </div>
              )}

              <div className=" break-normal text-[25px] font-medium text-black-400">
                <p>
                  {title}
                  {titleGreen && (
                    <>
                      {titleBreak && <br />}
                      <span className="text-primary"> {titleGreen}</span>
                    </>
                  )}
                </p>
                {description && (
                  <p className="mt-2 text-base text-black-400 md:min-w-max">
                    {description}
                  </p>
                )}
              </div>
              {tag && (
                <div className="my-4 flex flex-wrap gap-4 text-primary md:min-w-max">
                  {tags.map((text, index) => (
                    <div
                      key={index}
                      className="rounded-full bg-[#8EC8943B] px-6 py-2 text-base font-medium"
                    >
                      {text}
                    </div>
                  ))}
                </div>
              )}

              {submitted ? (
                <FormSubmitted />
              ) : (
                <form onSubmit={formik.handleSubmit} className="mx-0">
                  {/* Purpose */}
                  <div className={"flex w-full flex-col gap-1"}>
                    <Select
                      id="purpose"
                      name="purpose"
                      styles={customStyles}
                      options={selectOptions}
                      placeholder="* Purpose"
                      value={formik.values.purpose}
                      onChange={(val) => formik.setFieldValue("purpose", val)}
                      onBlur={() => formik.setFieldTouched("purpose", true)}
                      className={
                        "mt-12 w-[100%] text-ellipsis border-b-[1px] border-b-primary bg-transparent focus:outline-none"
                      }
                    />
                    {formik.touched.purpose && formik.errors.purpose && (
                      <p className="text-red-500">
                        {formik.errors.purpose as string}
                      </p>
                    )}
                  </div>

                  {/* Redirect if purpose = joinus */}
                  {formik.values.purpose?.value === "joinus" ? (
                    <div className={"flex w-full"}>
                      <Link
                        className={
                          "mt-10 rounded-md bg-green-300 px-10 py-2 text-[14px] font-bold text-white xl:bg-primary xl:px-12 xl:text-[16px] xl:font-normal"
                        }
                        to={"/join-us"}
                      >
                        Join us
                      </Link>
                    </div>
                  ) : (
                    <>
                      {/* Name & Company & Email & Phone */}
                      <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 xl:grid-cols-2">
                        {(
                          ["name", "companyName", "email"] as Array<
                            keyof typeof formik.values
                          >
                        ).map((field) => (
                          <div key={field} className="flex flex-col gap-1">
                            <input
                              name={field}
                              type={field === "email" ? "email" : "text"}
                              placeholder={`* ${
                                field === "companyName"
                                  ? "Company name"
                                  : field.charAt(0).toUpperCase() +
                                    field.slice(1)
                              }`}
                              value={(formik.values as any)[field]}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              className={
                                "mt-10 w-full border-b-[1px] border-b-primary bg-transparent p-1 focus:outline-none"
                              }
                            />
                            {formik.touched[
                              field as keyof typeof formik.touched
                            ] &&
                              (formik.errors as any)[field] && (
                                <p className="text-red-500">
                                  {(formik.errors as any)[field]}
                                </p>
                              )}
                          </div>
                        ))}

                        {/* Phone */}
                        <div className="flex flex-col gap-1">
                          <PhoneInput
                            name="phone"
                            placeholder="Enter phone number"
                            value={formik.values.phone as any}
                            defaultCountry={
                              (location?.countryCode as any) || "IN"
                            }
                            onChange={(val) => {
                              try {
                                const parsed = parsePhoneNumber(val || "");
                                const formattedPhone = `+${parsed.countryCallingCode} ${parsed.nationalNumber}`;
                                formik.setFieldValue("phone", formattedPhone);
                              } catch (e) {
                                formik.setFieldValue("phone", val);
                              }
                            }}
                            onBlur={() => formik.setFieldTouched("phone", true)}
                            className="input-phone-number mt-10 w-full border-b-[1px] border-b-primary bg-transparent p-1 focus:outline-none xl:w-full"
                            international
                            countryCallingCodeEditable={false}
                          />
                          {formik.touched.phone && formik.errors.phone && (
                            <p className="text-red-500">
                              {formik.errors.phone}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Message */}
                      <div className="mt-6">
                        <textarea
                          name="message"
                          rows={4}
                          placeholder="* Write a message..."
                          value={formik.values.message}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="mt-9 w-full resize-none rounded-xl border border-primary bg-transparent p-4 focus:outline-none"
                        />
                        {formik.touched.message && formik.errors.message && (
                          <p className="text-red-500">
                            {formik.errors.message}
                          </p>
                        )}
                      </div>

                      {/* How Did You Hear About Us */}
                      <div
                        className={"relative mt-9 flex w-full flex-col gap-1"}
                      >
                        <Select
                          name="heardFrom"
                          styles={customStyles}
                          options={heardFromOptions}
                          placeholder="* How did you hear about us?"
                          value={formik.values.heardFrom}
                          onChange={(val) =>
                            formik.setFieldValue("heardFrom", val)
                          }
                          onBlur={() =>
                            formik.setFieldTouched("heardFrom", true)
                          }
                          className={
                            "w-[100%] text-ellipsis border-b-[1px] border-b-primary bg-transparent focus:outline-none"
                          }
                          menuPosition={isModal ? "absolute" : "fixed"}
                          menuPlacement="auto"
                          menuPortalTarget={null}
                        />
                        {formik.touched.heardFrom &&
                          formik.errors.heardFrom && (
                            <p className="text-red-500">
                              {formik.errors.heardFrom as string}
                            </p>
                          )}
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={!formik.isValid || formik.isSubmitting}
                        className={`z-0 mt-9 flex items-center justify-center rounded px-10 py-2 font-semibold text-white drop-shadow-xs lg:mt-10 xl:mt-16 xl:text-[22px] xl:font-normal ${
                          !formik.isValid || formik.isSubmitting
                            ? "bg-[#ababab] cursor-not-allowed"
                            : "bg-green-300 hover:bg-green-400"
                        }`}
                      >
                        {formik.isSubmitting && (
                          <LoaderSvg width="w-4" height="h-4" />
                        )}
                        <span className="ml-2">
                          {formik.isSubmitting ? "Submitting..." : "Submit"}
                        </span>
                      </button>

                      {/* Error Message */}
                      {errorMsg && (
                        <p className="mt-3 text-red-600">{errorMsg}</p>
                      )}
                    </>
                  )}
                </form>
              )}
            </div>
          </div>

          <div className={"flex flex-1 "}>
            <div
              className={`mx-auto flex flex-col overflow-hidden ${
                isModal === true ? "" : " lg:mx-0"
              }`}
            >
              {!isModal && (
                <div className={"hidden xl:block"}>
                  <GeographicMapSvg />
                </div>
              )}
              <p className={"mt-[60px] text-center font-medium text-primary"}>
                Propelius Technologies
              </p>
              <p className={"text-center font-light text-[#111111]"}>
                Based in India&nbsp;
                <img src={heart} className={"inline"} alt={"heart icon"} />
                &nbsp; working worldwide
              </p>
            </div>
          </div>
        </div>
      </SecondaryWidthWrapper>
    </MaxWidthWrapper>
  );
};

export default ContactUs;
